import { User } from "firebase/auth";

// Re-export book types
export type { ReadingStatus, Book, GoogleBooksResult, BookGenre, CatalogBook } from "./books";
export { BOOK_GENRES } from "./books";

// Re-export movie types
export type { WatchStatus, Movie, TMDBSearchResult, CatalogMovie, MovieGenre } from "./movies";
export { MOVIE_GENRES } from "./movies";

// Re-export music types
export type { ListenStatus, MusicItem, CatalogMusic, MusicGenre } from "./music";
export { MUSIC_GENRES } from "./music";

// Re-export recipe types
export type { Recipe, RecipeCategory } from "./recipes";
export { RECIPE_CATEGORIES } from "./recipes";

// ---- Theme Types ----

export type ThemeColors = {
  backgroundColor: string;
  panelColor: string;
  selectedColor: string;
  border: string;
  color: string;
  altColor: string;
  hoverColor: string;
  secondaryColor: string;
  heroGradient: string;
  heroGradientLight?: string;
  textUnavailable?: string;
  todayColor?: string;
  toggleColor: string;
  createButton: string;
  /** Theme-aware text colour for hero overlay elements (mini week, etc.) */
  heroText?: string;
  /** 5-colour palette for workout types, race cards, etc. */
  palette?: [string, string, string, string, string];
};

export type ThemeMap = Record<string, ThemeColors>;

export type ThemeContextValue = {
  theme: ThemeMap;
  changeTheme: (newTheme: string) => void;
  activeTheme: string;
};

// ---- User Settings Types ----

export type NotificationPreferences = {
  tasks: boolean;       // deadline reminders, overdue alerts
  workouts: boolean;    // daily workout reminders for active gym/running plans
  social: boolean;      // community reactions, replies
  announcements: boolean; // admin announcements, feature updates
  engagement: boolean;  // nudges, empty-state reminders
  cycle: boolean;       // period predictions, daily log prompts, completion celebrations
  pill: boolean;        // daily pill reminder
  emails: boolean;      // welcome, nudge, and marketing emails
};

/** Defaults written to every new user doc — must mirror the cloud function
 *  fallbacks (`?? true`) so the doc state always agrees with server behaviour. */
export const DEFAULT_NOTIFICATION_PREFS: NotificationPreferences = {
  tasks: true,
  workouts: true,
  social: true,
  announcements: true,
  engagement: true,
  cycle: true,
  pill: true,
  emails: true,
};

export type ActivityTrackingPrefs = {
  pedometer: boolean;   // step counting during runs
  healthSync: boolean;  // sync with Health Connect / HealthKit
};

export type FitnessLevel = "beginner" | "some_experience" | "intermediate" | "advanced";

export type UserSettings = {
  theme: string;
  useIcons: boolean;
  notifications: boolean;
  notificationPrefs?: NotificationPreferences;
  navBarItems?: string[];
  hiddenExercises?: string[];
  weightUnit?: "lbs" | "kg" | "st";
  defaultRestTimer?: number;
  activityTracking?: ActivityTrackingPrefs;
  profilePhoto?: string; // Base64 data URL of resized profile photo
  fitnessLevel?: FitnessLevel;
  proteinTarget?: number;
  proteinGoal?: ProteinGoal;
  /** User's estimated daily calorie intake (kcal). Overrides the gender-based default of 2000/2500. */
  dailyCalorieIntakeOverride?: number;
  /** Calorie goal — what the daily intake target should achieve.
   *  Drives the goal-multiplier in the intake wizard (lose=0.8, maintain=1.0,
   *  gain=1.1). Lets the calorie target adapt as the user changes goals
   *  without them having to recompute manually. */
  calorieGoal?: "lose" | "maintain" | "gain";
  /** Activity level — how much you typically move beyond logged workouts.
   *  Drives the activity-multiplier in the intake wizard (sedentary=1.2,
   *  light=1.375, moderate=1.55, very_active=1.725 — Mifflin-St Jeor
   *  standard activity factors). */
  activityLevel?: "sedentary" | "light" | "moderate" | "very_active";
  /** Biological sex used for BMR calculation. Overrides onboarding gender. */
  biologicalSex?: "male" | "female";
  /** Height in centimetres — feeds the Mifflin-St Jeor BMR formula. */
  heightCm?: number;
  /** Age in years — feeds the Mifflin-St Jeor BMR formula. Overrides any age computed from dateOfBirth. */
  ageYears?: number;
  pillReminder?: {
    enabled: boolean;
    time: string;       // "HH:mm" e.g. "09:00" in user's local timezone
    timezone?: string;  // IANA timezone e.g. "Europe/London"
  };
  pillLog?: Record<string, boolean>; // { "2026-03-25": true, ... } — dates pill was taken
  contraceptionTracking?: {
    type: string;                     // "iud"|"implant"|"injection"|"patch"
    insertionDate?: string;           // ISO date — when it was fitted/inserted
    lastInjectionDate?: string;       // ISO date — for injection type
    lastPatchChangeDate?: string;     // ISO date — for patch type
  };
  /** Active fitness phase — drives unified weekly targets across calories,
   *  protein, lifting frequency, steps, and weigh-ins. Targets are snapshotted
   *  at phase-start so changes to maintenance calc don't silently move goalposts. */
  fitnessPhase?: FitnessPhase;
};

export type FitnessPhaseType = "bulk" | "cut" | "recomp" | "maintain";

export type FitnessPhaseTargets = {
  calories: number;        // kcal/day
  proteinG: number;        // grams/day
  liftsPerWeek: number;
  stepsPerDay: number;
  weighInsPerWeek: number;
};

export type FitnessPhase = {
  type: FitnessPhaseType;
  /** ISO timestamp — used to scope weekly progress queries. */
  startedAt: string;
  /** Snapshot of targets at phase-start. */
  targets: FitnessPhaseTargets;
  /** Optional per-target user overrides. Read merged in `getEffectivePhaseTargets`. */
  overrides?: Partial<FitnessPhaseTargets>;
};

export type UserSettingsContextValue = {
  settings: UserSettings;
  updateSettings: (newSettings: Partial<UserSettings>) => void;
};

// ---- User Types ----

export type Subscription = {
  plan: string;
  active: boolean;
  startDate: Date;
  endDate: Date;
  trialStartDate?: Date;
  trialEndDate?: Date;
  isTrial?: boolean;
};

// ---- Login Rewards Types ----

export type LoginRewards = {
  totalPoints: number;
  lastClaimDate: string | null;    // YYYY-MM-DD
  weekBonusClaimed: string | null; // ISO week key "2026-W10"
  claimedDates?: string[];         // all YYYY-MM-DD dates the user claimed
  scratchCardDiscount?: number;    // random discount won from scratch card (percentage)
  scratchCardsEarned?: number;     // how many scratch cards the user has earned so far
};

/** Scratch card prize pool — weighted random selection */
export type ScratchCardPrize = {
  discount: number;
  label: string;
  icon: string;
  weight: number; // higher = more likely
};

export const SCRATCH_CARD_PRIZES: ScratchCardPrize[] = [
  { discount: 5,   label: "5% off Pro",   icon: "fa-solid fa-tag",   weight: 40 },
  { discount: 10,  label: "10% off Pro",  icon: "fa-solid fa-tags",  weight: 28 },
  { discount: 15,  label: "15% off Pro",  icon: "fa-solid fa-fire",  weight: 18 },
  { discount: 20,  label: "20% off Pro",  icon: "fa-solid fa-gem",   weight: 9 },
  { discount: 25,  label: "25% off Pro",  icon: "fa-solid fa-crown", weight: 4 },
  { discount: 100, label: "1 Month Free", icon: "fa-solid fa-gift",  weight: 1 },
];

/** Points needed between each scratch card (first at 200, then every 200) */
export const SCRATCH_CARD_INTERVAL = 200;

export type RewardTier = {
  points: number;
  discount: number; // percentage off
  label: string;
  icon: string;
};

export const REWARD_TIERS: RewardTier[] = [
  { points: 200, discount: 5, label: "5% off Pro", icon: "fa-solid fa-tag" },
  { points: 400, discount: 10, label: "10% off Pro", icon: "fa-solid fa-tags" },
  { points: 800, discount: 15, label: "15% off Pro", icon: "fa-solid fa-fire" },
  { points: 2000, discount: 25, label: "25% off Pro", icon: "fa-solid fa-crown" },
  { points: 3500, discount: 100, label: "1 Month Free", icon: "fa-solid fa-gift" },
];

/** Free-tier usage limits */
export const FREE_TIER_LIMITS = {
  notes: 20,
  workoutPlans: 1,
  communityPostsPerDay: 3,
} as const;

/** Duration of the free trial in days */
export const TRIAL_DURATION_DAYS = 14;

export type UserDetails = {
  user: (User & { name?: string }) | null;
  dateCreated: string;
  dateCreatedRaw?: Date;
  admin: boolean;
  sharedCalendars: SharedCalendar[];
  familyMembers?: FamilyMember[];
  familyInvites?: FamilyInvite[];
  tester: boolean;
  subscription: Subscription | null;
  existingSubscription: boolean;
  subscriptionEndingSoon?: boolean;
  onboarding?: OnboardingData;
  loginRewards?: LoginRewards;
  referredBy?: { uid: string; name: string; code: string };
  earlyAdopter?: boolean;
  isBetaTester?: boolean;
  /** IANA timezone (e.g. "Europe/London") — captured from browser, used by scheduled functions for local-time comparisons. */
  timezone?: string;
  /** Geo metadata captured at signup (country, region, city, ip hash). Populated by captureSignupGeo cloud function. */
  signupGeo?: SignupGeo;
};

export type SignupGeo = {
  country?: string;       // ISO country code, e.g. "GB"
  countryName?: string;   // "United Kingdom"
  region?: string;        // "England"
  city?: string;          // "London"
  timezone?: string;      // resolved from IP
  isp?: string;
  ipHash?: string;        // SHA-256 truncated (privacy)
  capturedAt?: string;    // ISO timestamp
};

export type UserContextValue = {
  userDetails: UserDetails | null;
  setUserDetails: React.Dispatch<React.SetStateAction<UserDetails | null>>;
  refreshUserDetails: (overrideUser?: User) => Promise<void>;
  saveOnboarding: (data: OnboardingData) => Promise<void>;
  updateLoginRewards: (rewards: LoginRewards) => Promise<void>;
  authLoading: boolean;
  /** Sign in anonymously — used by quick onboarding flow to skip account creation. */
  signInAsGuest: () => Promise<void>;
  /** Link the current anonymous account to email/password — preserves UID + data. */
  linkAnonymousToEmail: (email: string, password: string) => Promise<void>;
  /** Link the current anonymous account to a Google account — preserves UID + data. */
  linkAnonymousToGoogle: () => Promise<void>;
};

// ---- Calendar Types ----

export type CalendarEntry = {
  id: string;
  userId: string;
  year: number;
  day: number;
  month: number;
  note: string;
  color: string;
  createdAt: string;
  showOnCalendar?: boolean;
  sharedWith?: string[];
};

export type Month = {
  name: string;
  value: number;
};

// ---- Menstrual Cycle Tracking Types ----

export type CycleSettings = {
  id: string;
  userId: string;
  enabled: boolean;
  avgCycleLength: number;
  avgPeriodLength: number;
  createdAt: string;
  updatedAt: string;
};

export type PeriodEntry = {
  id: string;
  userId: string;
  startDate: string; // ISO date string
  endDate: string | null;
  symptoms: string;
  flow: string; // "light" | "medium" | "heavy" | ""
  heaviestDay?: number; // 1-based day index (e.g. 2 = second day of period)
  createdAt: string;
};

export type PredictedPeriod = {
  startDate: Date;
  endDate: Date;
  confidence: number; // 0-1 scale
};

// ---- Cycle Day Log Types ----

export type CycleMood = "great" | "good" | "okay" | "low" | "terrible";
export type CycleEnergy = "high" | "medium" | "low";
export type CycleFlow = "spotting" | "light" | "medium" | "heavy";
export type CervicalMucus = "dry" | "sticky" | "creamy" | "egg-white" | "watery";
export type SexType = "protected" | "unprotected" | "none";
export type LibidoLevel = "high" | "medium" | "low" | "none";
export type DischargeType = "clear" | "white" | "yellow" | "brown" | "pink";
export type IntimacyPartner = "partner" | "solo" | "na";

export const MEDICATION_OPTIONS = [
  "Birth control pill",
  "IUD",
  "Pain meds",
  "Antidepressant",
  "Iron supplement",
  "Prenatal vitamin",
  "Antibiotic",
  "Other",
] as const;

export type CycleDayLog = {
  id: string;
  userId: string;
  date: string;                  // ISO date (YYYY-MM-DD) — one log per day
  symptoms: string[];            // e.g. ["Nausea", "Cramps"]
  activities: string[];          // e.g. ["Sex", "Exercise"]
  mood: CycleMood | null;
  energy: CycleEnergy | null;
  flow: CycleFlow | null;
  testResult: PregnancyTestResult | null;
  notes: string;
  // Fertility tracking fields
  bbt?: number | null;           // Basal body temperature
  bbtUnit?: "F" | "C";          // Temperature unit
  cervicalMucus?: CervicalMucus | null;
  sexType?: SexType | null;
  libido?: LibidoLevel | null;
  medications?: string[];        // Multi-select from MEDICATION_OPTIONS
  dischargeType?: DischargeType | null;
  waterIntake?: number | null;   // Glasses of water
  sleepQuality?: number | null;  // 1-5 rating
  // Intimacy tracking
  orgasm?: boolean | null;
  intimacyPartner?: IntimacyPartner | null;
  // Wearable-synced data
  wearableBBT?: number | null;         // Body temp from watch (Celsius)
  wearableRestingHR?: number | null;   // Resting HR from watch (bpm)
  wearableSleepMinutes?: number | null; // Total sleep duration from watch
  wearableSleepStages?: {              // Sleep stage breakdown from watch
    deep: number;
    light: number;
    rem: number;
    awake: number;
  } | null;
  wearableSource?: string | null;      // Device source name (e.g. "Samsung Health")
  createdAt: string;
  updatedAt: string;
};

// ---- Pregnancy Tracking Types ----

export type PregnancyTestResult = "positive" | "negative" | "faint_line" | "not_taken";

export type PregnancyStatus = "active" | "ended" | "loss" | "delivered";

export type EndPregnancyReason = "delivered" | "miscarriage" | "stillbirth" | "ectopic" | "other" | "prefer_not_to_say";

export type PregnancyData = {
  id: string;
  userId: string;
  status: PregnancyStatus;
  dueDate: string;
  lastPeriodDate: string;
  positiveTestDate: string;
  activatedAt: string;
  endedAt: string | null;
  endReason?: EndPregnancyReason;
  endWeek?: number;
  endNotes?: string;
  createdAt: string;
  updatedAt: string;
};

export type PregnancyLog = {
  id: string;
  userId: string;
  pregnancyId: string;
  date: string;
  weight: number | null;
  symptoms: string;
  mood: number | null;
  notes: string;
  createdAt: string;
};

export type PregnancyWeekInfo = {
  weeks: number;
  days: number;
  trimester: 1 | 2 | 3;
  totalDays: number;
  daysUntilDue: number;
  percentComplete: number;
  babySize: string;
};

export type PregnancyPhoto = {
  id: string;
  userId: string;
  pregnancyId: string;
  weekNumber: number;
  photoUrl: string;
  caption: string;
  uploadedAt: string;
};

export type BirthPlanPreferences = {
  birthLocation: string | null;        // "hospital" | "birth_centre" | "home" | null
  painRelief: string[];                // ["epidural", "gas_and_air", "water", "tens", "none"]
  birthPositions: string[];            // ["active", "water", "lying", "squatting", "kneeling"]
  skinToSkin: boolean | null;          // immediate skin-to-skin
  delayedCordClamping: boolean | null;
  feedingPlan: string | null;          // "breastfeed" | "formula" | "combination" | null
  birthPartner: string;               // free text
  musicOrAmbience: string;            // free text
  specialRequests: string;            // free text
};

export type HospitalBagItem = {
  id: string;
  label: string;
  category: "mum" | "baby" | "partner";
  packed: boolean;
  custom?: boolean;                    // user-added items
};

// ---- Note Types ----

export type Note = {
  docId?: string;
  title: string;
  details?: string;
  category?: string;
  image_url?: string;
  is_private?: boolean;
  date_created?: string;
  userId?: string;
  favourite?: boolean;
  sharedWith?: string[];
  linkedMediaId?: string;   // Firestore doc ID of linked movie/book
  mediaStatus?: string;     // synced status: "want_to_watch" | "watching" | "watched" etc.
};

// ---- Category Types ----

export type Category = {
  id: string;
  name: string;
  color: string;
  icon: string;
  order: number;
  isDefault: boolean;
  createdAt?: Date;
};

export type CategoryContextValue = {
  categories: Category[];
  addCategory: (category: Omit<Category, "id" | "createdAt">) => Promise<void>;
  updateCategory: (id: string, updates: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  getCategoryById: (id: string) => Category | undefined;
  getCategoryByName: (name: string) => Category | undefined;
  reorderCategories: (categoryIds: string[]) => Promise<void>;
  isLoading: boolean;
};

// ---- Task Types ----

export type RecurrenceRule = {
  type: "weekly" | "monthly" | "yearly";
  dayOfWeek?: number; // 0-6 (Sun-Sat) — for weekly
  dayOfMonth?: number; // 1-31 — for monthly
  month?: number; // 0-11 — for yearly (birthday)
  day?: number; // 1-31 — for yearly (birthday)
};

export type SubTask = {
  id: string;
  content: string;
  done: boolean;
};

export type TaskTemplate = {
  docId?: string;
  userId?: string;
  name: string;
  subtasks: SubTask[];
  category?: string | null;
  date_created?: string;
};

export type TaskDoc = {
  docId?: string;
  userId?: string;
  content: string;
  toggle?: boolean;
  date_created?: string;
  completedDate?: string | null;
  deadline?: string | null;
  deadlineTime?: string | null;
  reminderBefore?: number | null;
  recurrence?: RecurrenceRule | null;
  parentTaskId?: string | null;
  isRecurring?: boolean;
  showOnCalendar?: boolean;
  category?: string | null;
  repeatOnComplete?: boolean;
  sharedWith?: string[];
  subtasks?: SubTask[];
};

// ---- List Types ----

export type ListDoc = {
  docId?: string;
  title: string;
  is_private?: boolean;
  date_created?: string;
  dateCreated?: string | null;
  userId?: string;
};

export type ListItemDoc = {
  docId?: string;
  content: string;
  toggle?: boolean;
  date_created?: string;
  dateCreated?: string | null;
  listId?: string;
  completedDate?: string | null;
};

// ---- Shared Calendar Types ----

export type SharedCalendar = {
  userId: string;
  name?: string;
  email?: string;
  removalRequested?: boolean;
};

// ---- Family / Sharing Types ----

export type FamilyMemberTitle = "friend" | "partner" | "mum" | "dad" | "sister" | "brother" | "daughter" | "son" | "other";

export type FamilyMember = {
  userId: string;
  email: string;
  name?: string;
  title?: FamilyMemberTitle;
  addedAt: string;
  addedBy: string;
  status?: "pending" | "accepted";
};

export type FamilyInvite = {
  fromUserId: string;
  fromEmail: string;
  fromName: string;
  sentAt: string;
};

export type SharedTaskCompletion = {
  userId: string;
  name: string;
  completedAt: string;
};

export type SharedTask = {
  id?: string;
  title: string;
  description?: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  sharedWith: string[];
  completedBy: SharedTaskCompletion | null;
  status: "open" | "completed";
};

// ---- Paginated Resource Types ----

export type PaginatedResource<T = Record<string, unknown>> = {
  next?: string | null;
  results: T[];
};

// ---- JWT Types ----

export type JwtPayload = {
  exp: number;
  [key: string]: unknown;
};

// ---- Select Option ----

export type SelectOption = {
  value: string;
  label: string;
};

// ---- In-App Notification Types ----

export type InAppNotification = {
  id: string;
  title: string;
  body: string;
  type:
    | "overdue"
    | "today"
    | "reminder"
    | "info"
    | "nudge"
    | "streak"
    | "announcement"
    | "social";
  timestamp: Date;
  read: boolean;
};

export type NotificationContextValue = {
  notifications: InAppNotification[];
  unreadCount: number;
  addNotification: (
    title: string,
    body: string,
    type: InAppNotification["type"],
  ) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
};

// ---- Health/Wellness Types ----

export type SleepLog = {
  hours: number;
  quality: number;
  bedtime: string | null;
  wakeTime: string | null;
};

export type DailyLog = {
  docId?: string;
  userId: string;
  date: string;
  mood: number | null;
  energy: number | null;
  water: number;
  sleep: SleepLog | null;
  gratitude: string[];
  steps?: number;
  heartRate?: { latest: number; resting: number | null } | null;
  calories?: { total: number; wearable: number; gym: number; cardio: number } | null;
  updatedAt: string;
};

// ---- Fitness / Lift Tracking Types ----

export type LiftLog = {
  id?: string;
  userId: string;
  date: string;
  exercise: string;
  weight: number;
  reps?: number | null;
  sets?: number | null;
  setLogs?: number[] | null;   // per-set rep counts, e.g. [20, 15, 10]
  duration?: number | null; // seconds
  calories?: number | null;
  sessionId?: string | null;
  createdAt: string;
  sharedWith?: string[];
};

// ---- Cardio Tracking Types ----

export type CardioActivity = "running" | "cycling" | "swimming" | "walking" | "elliptical" | "rowing" | "stairClimber";

export type RoutePoint = {
  lat: number;
  lng: number;
  timestamp: number;
};

export type CardioLog = {
  id?: string;
  userId: string;
  date: string;
  activity: CardioActivity;
  distance: number;
  duration: number;
  calories?: number | null;
  createdAt: string;
  sharedWith?: string[];
  route?: RoutePoint[];
  // Gym machine fields
  exerciseLabel?: string;
  speed?: number | null;
  incline?: number | null;
  resistance?: number | null;
  // Plan metadata (set when run is from a training plan)
  planId?: string;
  planName?: string;
  planWeek?: number;
  runType?: string;
  workoutTitle?: string;
};

// ---- Daily Step Log Types ----

export type StepLogSource = "health" | "session" | "manual";

/**
 * Persistent daily step total. One doc per user per local-date,
 * keyed by `${userId}_${YYYY-MM-DD}` so re-syncs idempotently overwrite.
 * Sourced from Health Connect / HealthKit on native; from manual sessions on web.
 */
export type StepLog = {
  id?: string;
  userId: string;
  date: string;        // YYYY-MM-DD (local)
  steps: number;
  source: StepLogSource;
  /** ISO timestamp when this row was last written. */
  syncedAt: string;
};

// ---- Body Weight Tracking Types ----

export type WeightUnit = "lbs" | "kg" | "st";

export type BodyWeightLog = {
  id?: string;
  userId: string;
  date: string;
  weight: number;
  unit: WeightUnit;
  createdAt: string;
};

// ---- Running Shoe Types ----

export type RunningShoe = {
  id: string;
  userId: string;
  name: string;
  brand?: string;
  addedDate: string;
  totalKm: number;
  retiredAt?: string | null;
  isActive: boolean;
  maxKm: number;
};

// ---- Protein Log Types ----

export type ProteinStatus = "hit" | "partial" | "missed";
export type ProteinGoal = "gain" | "maintain" | "lose";

export type ProteinLog = {
  id?: string;
  userId: string;
  date: string;
  status: ProteinStatus;
  createdAt: string;
};

// ---- Food Log Types ----

export type FoodCategory =
  | "meat"
  | "fish"
  | "eggs_dairy"
  | "plant_protein"
  | "grains"
  | "potatoes"
  | "veg"
  | "fruit"
  | "breakfast"
  | "meals"
  | "snacks"
  | "sweets"
  | "drinks";

export type FoodItem = {
  id: string;
  name: string;
  category: FoodCategory;
  emoji: string;
  /** User-readable portion label, e.g. "1 breast (165g)" or "200g cooked". */
  portionLabel: string;
  /** kcal in one default portion. */
  kcalPerPortion: number;
  /** Protein in grams in one default portion. */
  proteinPerPortion: number;
};

export type FoodLogEntry = {
  id?: string;
  userId: string;
  date: string;             // YYYY-MM-DD
  /** Catalogue id, or null for a custom entry. */
  foodId: string | null;
  /** Free-text label for custom entries; otherwise echoes catalogue name for resilience to catalogue rename. */
  name: string;
  /** Portion multiplier applied to catalogue portion (1 = single portion, 2 = double, 0.5 = half). For custom entries always 1. */
  portionMultiplier: number;
  /** Computed at log time so future catalogue tweaks don't rewrite history. */
  kcal: number;
  protein: number;
  createdAt: string;
};

// ---- Progress Photo Types ----

export type PhotoAngle = "front" | "side-left" | "side-right" | "back";

export type ProgressPhoto = {
  id: string;
  userId: string;
  date: string; // YYYY-MM-DD
  angle: PhotoAngle;
  localData: string; // base64 data URL
  cloudinaryUrl?: string; // only if premium + synced
  weight?: number;
  weightUnit?: WeightUnit;
  note?: string;
  createdAt: string;
};

// ---- Workout Template & Session Types ----

export type WorkoutTemplateExercise = {
  type: "weight" | "cardio";
  exerciseId: string;
  label: string;
  supersetGroup?: number;
  sets?: number;
  reps?: string;
  restSeconds?: number;
  notes?: string;
};

export type WorkoutLocation = "gym" | "home";

export type WorkoutTargetGoal =
  | "chest" | "back" | "shoulders" | "arms" | "legs" | "glutes" | "core";

export type WorkoutTemplate = {
  id: string;
  name: string;
  icon: string;
  exercises: WorkoutTemplateExercise[];
  sharedBy?: string;
  location?: WorkoutLocation;
  targetGoal?: WorkoutTargetGoal;
  /** When set, template is surfaced first for users of this gender */
  targetGender?: "male" | "female";
};

export type WorkoutSessionStatus = "active" | "completed" | "abandoned";

export type ExerciseSessionNote = {
  skipped?: boolean;
  difficulty?: "easy" | "good" | "hard" | "failed";
  weightUsed?: number;
  repsDone?: number;
  setsDone?: number;
  setLogs?: number[];   // per-set rep counts, e.g. [6, 6, 4]
  swappedFrom?: string;
  note?: string;        // freeform user note — surfaces on next attempt
};

export type WorkoutSession = {
  id?: string;
  userId: string;
  date: string;
  templateId: string;
  templateName: string;
  startedAt: string;
  completedAt?: string | null;
  durationMinutes: number;
  completedExercises: string[];
  totalExercises: number;
  status: WorkoutSessionStatus;
  createdAt: string;
  sharedWith?: string[];
  notes?: string;
  exerciseNotes?: Record<string, ExerciseSessionNote>;
  /** Map original-exerciseId → replacement chosen during this session. Mirrors local modal state so a swap survives minimize/resume and lets us update the plan on finish. */
  swappedExercises?: Record<string, { id: string; label: string }>;
  caloriesBurned?: number;
};

// ---- Payment Types ----

export type PaymentRecord = {
  docId?: string;
  userId: string;
  planId: string;
  planName: string;
  amount: number;
  currency: string;
  paidAt: Date;
  months: number | "lifetime";
  isTest?: boolean;
  type?: "subscription" | "tip";
};

export type ExpenseCategory = "marketing" | "hosting" | "tools" | "design" | "legal" | "other";

export type ExpenseRecord = {
  docId?: string;
  category: ExpenseCategory;
  description: string;
  amount: number;          // stored in pence
  currency: string;
  date: string;            // ISO date string YYYY-MM-DD
  platform?: string;       // e.g. "Facebook Ads", "Google Ads", "Netlify"
  recurring?: boolean;
  isTest?: boolean;
};

// ---- Referral Types ----

export type ReferralStatus = "pending" | "converted" | "cleared" | "revoked" | "denied";

export type Referral = {
  id?: string;
  referrerUid: string;        // user who shared the code
  referredUid: string;        // user who signed up with the code
  referralCode: string;       // code used
  createdAt: string;          // ISO date when referral was created
  convertedAt: string | null; // ISO date when referred user first subscribed
  status: ReferralStatus;
  // Hold period & lifecycle fields
  holdClearedAt?: string | null;   // ISO date when hold passed
  revokedAt?: string | null;       // ISO date when subscription cancelled/refunded
  revokedReason?: "cancelled" | "refunded" | "chargeback" | "fraud";
  stripeSessionId?: string;        // Stripe checkout session ID for audit
  billingCyclesCompleted?: number;  // number of successful payments by referred user
  fraudFlags?: string[];           // e.g. ["same-device", "velocity"]
};

export type PayoutRequest = {
  id?: string;
  userId: string;
  amount: number;             // pence (GBP)
  status: "pending" | "approved" | "paid" | "rejected";
  paypalEmail?: string;
  createdAt: string;
  processedAt: string | null;
  adminNote?: string;
  serverVerifiedAmount?: number;  // amount verified by server-side calculation
};

// ---- Onboarding Types ----

export type PrimaryUseCase =
  | "housework"
  | "notes"
  | "notesandtasks"
  | "health"
  | "wellness"
  | "weightloss"
  | "fitness"
  | "running"
  | null;

export type OnboardingGender = "female" | "male" | "neutral";

export type OnboardingPreferences = {
  primaryUseCase: PrimaryUseCase;
  gender?: OnboardingGender;
  focusMode?: boolean;
  notes: boolean;
  tasks: boolean;
  calendar: boolean;
  health: {
    enabled: boolean;
    mood: boolean;
    water: boolean;
    sleep: boolean;
    cycle: boolean;
    gratitude: boolean;
    fitness: boolean;
    running: boolean;
  };
  books: boolean;
  notifications: boolean;
  virtualPet?: {
    enabled: boolean;
    species: string;
    name: string;
  };
  collection?: {
    enabled: boolean;
  };
  // Personalised onboarding answers
  womensHealthGoals?: string[];   // ["period","pregnancy","body","intimacy","weight","wellness"]
  lastPeriodDate?: string;        // ISO date from onboarding
  typicalCycleLength?: number;    // 21-35 days
  typicalPeriodLength?: number;   // 2-8 days
  age?: number;
  dateOfBirth?: string;           // ISO date YYYY-MM-DD
  // Women's health screening
  healthScreening?: {
    medicalConditions?: string[];  // ["pcos","endometriosis","fibroids","thyroid","diabetes","none"]
    contraception?: string;        // "none"|"pill"|"iud"|"implant"|"injection"|"patch"|"condom"|"other"
    pregnancyHistory?: string;     // "never"|"currently"|"past"|"trying"
    regularity?: string;           // "regular"|"irregular"|"unsure"
  };
  // Canonical outcomes match GymProgramGoal: "build_muscle"|"weight_loss"|"tone_up"|"strength"|"stamina"|"general_fitness".
  // Meta: "track"|"program". Legacy values still in the wild: "muscle"|"weight"|"holiday" — normalise via normalizeFitnessGoal.
  fitnessGoal?: string;
  onboardingFitnessLevel?: string; // "beginner"|"intermediate"|"advanced"
  runningGoal?: string;           // "fit"|"race"|"track"|"walking"
  runningLevel?: string;          // "starting"|"casual"|"regular"|"experienced"
  raceTarget?: string;            // "5k"|"10k"|"half"|"marathon"
  howFoundUs?: string;            // "facebook"|"instagram"|"tiktok"|"google"|"friend"|"other" — still read by admin analytics for legacy users
};

export type OnboardingData = {
  completed: boolean;
  preferences: OnboardingPreferences;
  completedAt?: string;
  /** True when the user skipped onboarding via the "Skip for now" link on welcome.
   *  Useful for admin analytics to distinguish completers from skippers. */
  skipped?: boolean;
};

// ---- Feature Requests ----

export type FeatureRequest = {
  docId?: string;
  userId: string;
  email: string;
  title: string;
  description: string;
  status: "new" | "reviewed" | "planned" | "done";
  createdAt: string;
};

// ---- Support Tickets ----

export type SupportTicket = {
  id?: string;
  userId: string;
  email: string;
  subject: string;
  message: string;
  category: "bug" | "account" | "feature" | "billing" | "other";
  status: "open" | "in-progress" | "resolved" | "closed";
  adminNotes?: string;
  platform?: string;
  appVersion?: string;
  createdAt: string;
  updatedAt?: string;
};

// ---- Pricing Plan Types ----

export type PricingPlan = {
  id: string;
  name: string;
  price: number; // Amount in pence (GBP)
  months: number | "lifetime";
  active: boolean;
};

// ---- Discount Codes ----

export type DiscountCode = {
  id: string;
  code: string;            // uppercase, e.g. "INSTA20"
  discountPercent: number; // 20 = 20% off
  maxUses: number | null;  // null = unlimited
  usedCount: number;
  usedBy: string[];        // user IDs — enforce one-per-user
  validFrom: string;       // ISO date
  validUntil: string | null; // null = no expiry
  active: boolean;
  excludeLifetime: boolean; // always true for now
  note: string;            // e.g. "Instagram March promo"
  createdAt: string;
};

// ---- Subscription Feature Gates ----

export type SubscriptionFeature =
  | "trends"
  | "insights"
  | "charts"
  | "export"
  | "unlimitedNotes"
  | "weeklySummary"
  | "planner"
  | "advancedPlans"
  | "virtualPet"
  | "discover"
  | "sharing";

// ---- Feature Flags ----

export type FeatureFlag = {
  id: string;
  name: string;
  enabledProd: boolean;
  enabledTest: boolean;
  description: string;
  lastToggledAt?: string;
  lastToggledBy?: string;
  createdAt?: string;
};

export type FeatureAccess = "prod" | "test" | "disabled";

export type FeatureFlagContextValue = {
  flags: FeatureFlag[];
  isFeatureEnabled: (name: string) => boolean;
  getFeatureAccess: (name: string) => FeatureAccess;
  toggleFlag: (
    id: string,
    field: "enabledProd" | "enabledTest",
    value: boolean,
  ) => Promise<void>;
  isLoading: boolean;
};

// ---- Community Types ----

export type CommunityChannel = "cycle" | "fitness" | "wellness" | "general";

export type ReactionType = "helpful" | "heart" | "relatable";

export type ReactionCounts = Record<ReactionType, number>;

export type InsightCardData = {
  type:
    | "cycle_avg"
    | "mood_avg"
    | "water_streak"
    | "sleep_avg"
    | "lift_pr"
    | "energy_trend";
  label: string;
  value: string;
  emoji: string;
};

export type PostAttachmentType = "image" | "workout" | "run" | "cycle" | "pregnancy";

export type PostAttachment = {
  type: PostAttachmentType;
  /** Cloudinary URL for image attachments */
  imageUrl?: string;
  /** Display label e.g. "Push Day", "5K Run", "Week 24" */
  label: string;
  /** Key-value stats to show e.g. { "Distance": "5.2 km", "Pace": "5'24\"" } */
  stats?: Record<string, string>;
  /** Icon class for the attachment badge */
  icon?: string;
  /** Formatted date string for card header */
  dateLabel?: string;
  /** GPS route for run/walk map */
  route?: [number, number][];
  /** Raw data for rendering exact share cards */
  rawData?: {
    // Run
    distance?: number;
    duration?: number;
    calories?: number;
    date?: string;
    activity?: string;
    userName?: string;
    // Cycle
    cycleDay?: number;
    avgCycleLength?: number;
    phase?: string;
    daysUntilNextPeriod?: number;
    // Pregnancy
    weekNumber?: number;
    trimester?: number;
    babySize?: string;
    daysUntilDue?: number;
    percentComplete?: number;
    dueDate?: string;
  };
};

export type PollOption = {
  id: string;
  text: string;
  votes: number;
};

export type PollData = {
  options: PollOption[];
  /** Total votes cast */
  totalVotes: number;
  /** Whether users can select multiple options */
  multiSelect?: boolean;
  /** Vote counts per option id — source of truth for display */
  voteCounts?: Record<string, number>;
};

export type CommunityPost = {
  id: string;
  userId: string;
  anonymousName: string;
  channel: CommunityChannel;
  title: string;
  body: string;
  insightCard: InsightCardData | null;
  reactionCounts: ReactionCounts;
  commentCount: number;
  reportCount: number;
  hidden: boolean;
  createdAt: string;
  /** Official post from OnyxLife (admin only) */
  isOfficial?: boolean;
  /** Author's profile photo (base64 data URL) */
  authorPhoto?: string;
  /** Post attachments (images, activities) */
  attachments?: PostAttachment[];
  /** Poll data — if present, post is a poll */
  poll?: PollData;
  /** Group this post belongs to (if any) */
  groupId?: string;
};

export type CommunityGroup = {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  createdBy: string;
  createdAt: string;
  memberCount: number;
  isPublic: boolean;
  /** Group banner/cover image URL */
  imageUrl?: string;
};

export type CommunityGroupMember = {
  id: string;
  groupId: string;
  userId: string;
  role: "owner" | "member";
  joinedAt: string;
};

export type CommunityComment = {
  id: string;
  postId: string;
  userId: string;
  anonymousName: string;
  body: string;
  createdAt: string;
  /** Commenter's profile photo (base64 data URL) */
  authorPhoto?: string;
};

export type CommunityProfile = {
  userId: string;
  anonymousName: string;
  /** User-chosen display name (replaces anonymous name when set) */
  displayName?: string;
  /** Short bio */
  bio?: string;
  /** Profile photo (base64 data URL) */
  profilePhoto?: string;
  /** Post with real display name instead of anonymous name */
  useRealName?: boolean;
  channelPreferences?: CommunityChannel[];
  unlockedAt: string;
  createdAt: string;
};

export type ReportReason =
  | "inappropriate"
  | "spam"
  | "harassment"
  | "misinformation";

export type CommunityActivityCheck = {
  label: string;
  icon: string;
  done: boolean;
};

export type CommunityAccessStatus = {
  unlocked: boolean;
  reason?: string;
  activities: CommunityActivityCheck[];
};

/* ── Habit Tracker ── */

export type HabitFrequency = "daily" | "weekdays" | "weekly";

export interface Habit {
  id: string;
  userId: string;
  name: string;
  icon: string;
  color: string;
  frequency: HabitFrequency;
  targetDays?: number[];
  completions: Record<string, boolean>;
  streak: number;
  bestStreak: number;
  createdAt: string;
}
